import {
  Box,
  Checkbox,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@atgfr8/atg-ui-tailwind';
import { ArrowRightIcon } from '@atgfr8/icons';
import { Button, SelectInput, ToastType, PhoneInput } from '@components';
import { CHANGE_TRACKING_SOURCES } from '@changeTracking/utils/changeTrackingRegistry';
import { SiteTitle } from '@components/SiteTitle/SiteTitle';
import { USER_FEATURE_NAMES } from '@constants/featureFlags';
import { createForm } from '@felte/solid';
import { validator } from '@felte/validator-yup';
import { useNavigate } from '@solidjs/router';
import { getEffectiveIsEnabledForUserByFeatureName } from '@store/Global/service';
import { createProspectAsCustomer } from '@store/prospect';
import { getCustomerLookup } from '@store/prospect/services';
import { userStore } from '@store/user';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { routeProspectDetails } from '@utils/routeProspectDetails';
import { handleToast } from '@utils/utils';
import { debounce, isEmpty } from 'lodash';
import { createSignal, onMount, Show } from 'solid-js';
import * as yup from 'yup';

const validationSchema = yup.object().shape({
  name: yup
    .string()
    .required('Customer Name is required')
    .matches(/[a-zA-Z].*[a-zA-Z]/, 'Name must contain at least two letters')
    .min(3, 'Customer Name must be at least 3 characters'),
  status: yup.string().required('Status is required'),
  public: yup.boolean(),
  contactName: yup.string().required('Contact Name is required'),
  contactEmail: yup.string().email('Invalid email').nullable(),
  contactPhone: yup
    .string()
    .matches(/^\d{10}$/, 'Value must be a valid 10-digit phone number')
    .nullable(),
  contactFax: yup.string().when((value) => {
    return isEmpty(value[0])
      ? yup.string().nullable().notRequired()
      : yup.string().min(10, 'Fax number must be at least 10 digits');
  }),
  prospectComment: yup.string(),
});

type InitialCustomerProspectFormValueTypes = {
  name: string;
  status: string;
  public: boolean;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  contactFax: string;
  prospectComment: string;
};

type ExistingCustomerLookupItem = {
  name: string;
  status: string;
};

const prospectCustomerStatus = [
  { label: 'Lead', value: 'Lead' },
  { label: 'Contacted', value: 'Contacted' },
  { label: 'Quoting', value: 'Quoting' },
  { label: 'Dead Lead', value: 'Dead Lead' },
  { label: 'Submitted', value: 'Submitted' },
  { label: 'Approved', value: 'Approved' },
];

const initialFormValues: InitialCustomerProspectFormValueTypes = {
  name: '',
  status: 'Lead',
  public: true,
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  contactFax: '',
  prospectComment: '',
};

const NewProspectPage = () => {
  const [createdProspectId, setCreatedProspectId] = createSignal();
  const [existingCustomers, setExistingCustomers] = createSignal<
    ExistingCustomerLookupItem[]
  >([]);
  const [isLookingUp, setIsLookingUp] = createSignal(false);
  const [isCreating, setIsCreating] = createSignal(false);
  const [isProspectFuzzyApiEnabled, setIsProspectFuzzyApiEnabled] =
    createSignal<boolean | null>(null);
  const navigate = useNavigate();

  onMount(async () => {
    const isEnabled =
      (await getEffectiveIsEnabledForUserByFeatureName(
        USER_FEATURE_NAMES.prospectFuzzyApi,
      )) ?? false;

    setIsProspectFuzzyApiEnabled(isEnabled);
  });

  const { form, errors, data, setFields, reset } =
    createForm<InitialCustomerProspectFormValueTypes>({
      initialValues: {
        ...initialFormValues,
      },
      onSubmit: async () => {
        await handleSubmit();
      },
      extend: validator({ schema: validationSchema }),
    });

  const lookUpCustomer = async (name: string) => {
    try {
      const response = await getCustomerLookup({
        name,
      });

      if (response?.isSuccess) {
        setExistingCustomers(response.value);
        return;
      }

      setExistingCustomers([]);
    } finally {
      setIsLookingUp(false);
    }
  };

  const debouncedLookUpCustomer = debounce((name: string) => {
    void lookUpCustomer(name);
  }, 300);

  const handleCustomerNameChange = (val: string) => {
    setFields('name', val);

    if (isProspectFuzzyApiEnabled() !== false) {
      setExistingCustomers([]);
      setIsLookingUp(false);
      return;
    }

    if (val.length <= 4) {
      setExistingCustomers([]);
      setIsLookingUp(false);
      return;
    }

    setIsLookingUp(true);
    debouncedLookUpCustomer(val);
  };

  const handleSubmit = async () => {
    setIsCreating(true);
    const payload = {
      operationType: 'Insert',
      id: 0,
      name: data().name,
      status: data().status,
      public: data().public,
      createdById: userStore.user.id,
      notes: [
        {
          userId: userStore.user.id,
          comment: data().prospectComment,
        },
      ],
      contacts: [
        {
          operationType: 'Insert',
          name: data().contactName,
          phone1: data().contactPhone,
          fax: data().contactFax,
          email: data().contactEmail,
        },
      ],
    };
    const resp = await createProspectAsCustomer(payload, [
      CHANGE_TRACKING_SOURCES.prospectCreate.customer,
      CHANGE_TRACKING_SOURCES.prospectCreate.contact,
    ]);
    if (resp && resp.isSuccess) {
      setCreatedProspectId(resp.value.id);
      handleToast(ToastType.Success, 'Prospect created successfully');
    }
    setIsCreating(false);
  };

  const cancelOrReset = () => {
    reset();
    setCreatedProspectId(null);
  };

  return (
    <>
      <SiteTitle title={'New Prospect'} hidePrefix={true} />
      <Stack
        spacing={1.4}
        p={2}
        backgroundColor="#E1ECF2"
        alignItems="center"
        height="100%"
        class="overflow-y-auto"
      >
        <Paper elevation={3} class="w-100 p-3 relative">
          <form ref={form}>
            <Typography
              variant="body1"
              class="text-xl font-medium! text-black pb-2"
            >
              Customer
            </Typography>
            <Stack spacing={2}>
              <TextField
                label="Name"
                value={data().name}
                onChange={handleCustomerNameChange}
                required
                error={errors().name}
                maxLength={50}
                dataTestId={'name-textInput'}
              />
              <SelectInput
                value={data().status}
                label="Status"
                onChange={(value) => {
                  setFields('status', value);
                }}
                menuItems={prospectCustomerStatus.map((s) => ({
                  label: s.label,
                  value: String(s.value),
                }))}
                dataTestId={formatDataTestId('Status', 'selectInput')}
              />
              <Checkbox
                size="xs"
                color="info"
                label="Public: Can be seen by everyone in my office"
                checked={data().public}
                onChange={(checked) => {
                  setFields('public', checked);
                }}
                dataTestId={'public-checkboxInput'}
              />
              <Show
                when={
                  isProspectFuzzyApiEnabled() === false &&
                  (isLookingUp() || existingCustomers().length > 0)
                }
              >
                <Box class="rounded border border-[#d9d9d9] bg-[#fafafa] p-3">
                  <Typography
                    variant="body1"
                    class="text-base font-medium! text-black"
                  >
                    Existing Customer Matches
                  </Typography>
                  <Divider class="my-2!" />
                  <Show
                    when={!isLookingUp()}
                    fallback={
                      <Typography variant="body2">Looking up...</Typography>
                    }
                  >
                    <Show
                      when={existingCustomers().length < 8}
                      fallback={
                        <Typography variant="body2" class="text-[#8a6d3b]">
                          Results returned too many matches to display...
                        </Typography>
                      }
                    >
                      <Stack spacing={1}>
                        {existingCustomers().map((customer) => (
                          <Box class="rounded bg-white p-2">
                            <Typography
                              variant="body1"
                              class="font-medium! text-black"
                            >
                              {customer.name}
                            </Typography>
                            <Typography variant="body2">
                              Status: {customer.status}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Show>
                  </Show>
                </Box>
              </Show>
            </Stack>
            <Divider class="pt-2! mb-2!" />
            <Typography
              variant="body1"
              class="text-xl font-medium! text-black pb-2"
            >
              Contact
            </Typography>
            <Stack spacing={2}>
              <TextField
                label="Name"
                value={data().contactName}
                onChange={(val: string) => setFields('contactName', val)}
                required
                error={errors().contactName}
                maxLength={50}
                dataTestId={'contact-name-textInput'}
              />
              <TextField
                label="Email"
                type="email"
                value={data().contactEmail}
                onChange={(val: string) => setFields('contactEmail', val)}
                error={errors().contactEmail}
                dataTestId={'contact-email-textInput'}
              />
              <PhoneInput
                label="Phone"
                placeholder="Phone Number"
                value={data().contactPhone}
                onChange={(value) => setFields('contactPhone', value)}
                error={errors().contactPhone}
                dataTestId={'contact-phone-phoneInput'}
              />
              <PhoneInput
                label="Fax"
                value={data().contactFax}
                onChange={(e) => setFields('contactFax', e)}
                error={errors().contactFax}
                dataTestId={'contact-fax-phoneInput'}
              />
              <TextField
                multiline
                rows={3}
                label="Notes"
                value={data().prospectComment}
                onChange={(itm: string) => setFields('prospectComment', itm)}
                dataTestId={'notes-textareafield'}
              />
              <Box class=" flex justify-end">
                <Show
                  when={createdProspectId()}
                  fallback={
                    <Button
                      type="submit"
                      label="Create"
                      class="mr-2!"
                      disabled={isCreating()}
                      dataTestId={formatDataTestId('Create', 'button')}
                    />
                  }
                >
                  <Button
                    onClick={() => {
                      if (routeProspectDetails(String(createdProspectId()))) {
                        navigate(`/Prospect/Details/${createdProspectId()}`);
                      }
                    }}
                    label="View Profile"
                    endIcon={<ArrowRightIcon size={20} />}
                    color="success"
                    class="mr-2!"
                    dataTestId={formatDataTestId('View Profile', 'button')}
                  />
                  <Button
                    onClick={cancelOrReset}
                    label="Reset"
                    class="mr-2!"
                    variant="outlined"
                    dataTestId={formatDataTestId('Reset', 'button')}
                  />
                </Show>
                <Button
                  variant="outlined"
                  onClick={cancelOrReset}
                  label="Cancel"
                  disabled={isCreating()}
                  dataTestId={formatDataTestId('Cancel', 'button')}
                />
              </Box>
            </Stack>
          </form>
        </Paper>
      </Stack>
    </>
  );
};

export default NewProspectPage;
